import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { WillDocument } from '../types/will';
import toast from 'react-hot-toast';

export const useWills = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch wills from database
  const {
    data: wills = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['wills', user?.id],
    queryFn: async () => {
      console.log('Fetching wills...');

      // First, check if user has a wallet address
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('wallet_address, email')
        .eq('id', user?.id)
        .single();
        
      if (userError) {
        console.error('Error fetching user data:', userError);
      }
      
      const userWalletAddress = userData?.wallet_address || user?.walletAddress;
      const userEmail = userData?.email || user?.email;
      
      console.log('User wallet address:', userWalletAddress);
      console.log('User email:', userEmail);

      // Fetch wills created by the user
      const { data: createdWills, error: createdError } = await supabase
        .from('wills')
        .select(`
          *,
          witnesses:will_witnesses(id, address, name, email, role, added_at, last_confirmation_at, is_active, public_key),
          beneficiaries:will_beneficiaries(id, address, name, email, relationship, share_percentage, access_level, added_at, is_active),
          access_grants:will_access_grants(id, grantee_address, grantee_name, grantee_email, access_level, granted_at, expires_at, is_active, conditions)
        `)
        .eq('creator_user_id', user?.id);

      if (createdError) {
        console.error('Error fetching created wills:', createdError);
        throw new Error(createdError.message);
      }

      // Fetch wills where user is a witness
      const { data: witnessWills, error: witnessError } = await supabase
        .from('will_witnesses')
        .select(`
          will:will_id(
            *,
            witnesses:will_witnesses(id, address, name, email, role, added_at, last_confirmation_at, is_active, public_key),
            beneficiaries:will_beneficiaries(id, address, name, email, relationship, share_percentage, access_level, added_at, is_active),
            access_grants:will_access_grants(id, grantee_address, grantee_name, grantee_email, access_level, granted_at, expires_at, is_active, conditions)
          )
        `)
        .or(`address.eq.${userWalletAddress},email.eq.${userEmail}`)
        .is('is_active', true);

      if (witnessError) {
        console.error('Error fetching witness wills:', witnessError);
      }

      // Fetch wills where user is a beneficiary
      const { data: beneficiaryWills, error: beneficiaryError } = await supabase
        .from('will_beneficiaries')
        .select(`
          will:will_id(
            *,
            witnesses:will_witnesses(id, address, name, email, role, added_at, last_confirmation_at, is_active, public_key),
            beneficiaries:will_beneficiaries(id, address, name, email, relationship, share_percentage, access_level, added_at, is_active),
            access_grants:will_access_grants(id, grantee_address, grantee_name, grantee_email, access_level, granted_at, expires_at, is_active, conditions)
          )
        `)
        .or(`address.eq.${userWalletAddress},email.eq.${userEmail}`)
        .is('is_active', true);

      if (beneficiaryError) {
        console.error('Error fetching beneficiary wills:', beneficiaryError);
      }

      console.log('Created wills:', createdWills);
      console.log('Witness wills:', witnessWills);
      console.log('Beneficiary wills:', beneficiaryWills);

      // Combine all wills and remove duplicates
      const allWills = [
        ...(createdWills || []),
        ...(witnessWills?.map(w => w.will) || []).filter(Boolean),
        ...(beneficiaryWills?.map(w => w.will) || []).filter(Boolean)
      ];

      // Remove duplicates by ID
      const uniqueWills = Array.from(
        new Map(allWills.map(will => [will.id, will])).values()
      );

      console.log('Combined unique wills:', uniqueWills);

      // Transform database records to WillDocument type
      const transformedWills = uniqueWills.map(will => ({
        id: will.id,
        title: will.title,
        description: will.description,
        documentId: will.document_id,
        creatorAddress: will.creator_address,
        creatorName: will.creator_name,
        createdAt: new Date(will.created_at),
        updatedAt: new Date(will.updated_at),
        isActive: will.is_active,
        executionConditions: [], // Would need separate table for this
        witnesses: (will.witnesses || []).map((w: any) => ({
          id: w.id,
          address: w.address,
          name: w.name,
          email: w.email,
          role: w.role,
          addedAt: new Date(w.added_at),
          lastConfirmationAt: w.last_confirmation_at ? new Date(w.last_confirmation_at) : undefined,
          isActive: w.is_active,
          publicKey: w.public_key
        })),
        beneficiaries: (will.beneficiaries || []).map((b: any) => ({
          id: b.id,
          address: b.address,
          name: b.name,
          email: b.email,
          relationship: b.relationship,
          sharePercentage: b.share_percentage,
          accessLevel: b.access_level,
          addedAt: new Date(b.added_at),
          isActive: b.is_active
        })),
        accessGrants: (will.access_grants || []).map((g: any) => ({
          id: g.id,
          granteeAddress: g.grantee_address,
          granteeName: g.grantee_name,
          granteeEmail: g.grantee_email,
          accessLevel: g.access_level,
          grantedAt: new Date(g.granted_at),
          expiresAt: g.expires_at ? new Date(g.expires_at) : undefined,
          isActive: g.is_active,
          conditions: g.conditions
        })),
        metadata: {
          version: will.version,
          lastWitnessCheck: will.last_witness_check ? new Date(will.last_witness_check) : undefined,
          executionStatus: will.execution_status,
          executionDate: will.execution_date ? new Date(will.execution_date) : undefined,
          executorAddress: will.executor_address,
          notificationSettings: will.notification_settings || {
            witnessReminders: true,
            beneficiaryNotifications: true,
            executorAlerts: true
          }
        },
        // Add reference to creator user for easier access
        creatorUser: {
          id: will.creator_user_id
        }
      })) as WillDocument[];

      console.log('Transformed wills:', transformedWills);
      return transformedWills;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Create will mutation
  const createWillMutation = useMutation({
    mutationFn: async (willData: {
      title: string;
      description?: string;
      documentId: string;
      creatorAddress: string;
      creatorName: string;
      witnesses: Array<{ address: string; name: string; email?: string; role: string }>;
      beneficiaries: Array<{ address: string; name: string; email?: string; relationship: string; sharePercentage: number }>;
    }) => {
      console.log('Creating will...', willData);

      // Start a transaction to create will and related records
      const { data: will, error: willError } = await supabase
        .from('wills')
        .insert({
          title: willData.title,
          description: willData.description,
          document_id: willData.documentId,
          creator_address: willData.creatorAddress,
          creator_name: willData.creatorName,
          creator_user_id: user?.id,
          is_active: true,
          execution_status: 'active',
          version: 1,
          notification_settings: {
            witnessReminders: true,
            beneficiaryNotifications: true,
            executorAlerts: true
          },
          metadata: {}
        })
        .select()
        .single();

      if (willError) {
        console.error('Error creating will:', willError);
        throw new Error(willError.message);
      }

      const willId = will.id;

      // Insert witnesses
      if (willData.witnesses.length > 0) {
        const { error: witnessError } = await supabase
          .from('will_witnesses')
          .insert(
            willData.witnesses.map(witness => ({
              will_id: willId,
              address: witness.address,
              name: witness.name,
              email: witness.email,
              role: witness.role,
              is_active: true
            }))
          );

        if (witnessError) {
          console.error('Error creating witnesses:', witnessError);
          throw new Error(`Failed to add witnesses: ${witnessError.message}`);
        }
      }

      // Insert beneficiaries
      if (willData.beneficiaries.length > 0) {
        const { error: beneficiaryError } = await supabase
          .from('will_beneficiaries')
          .insert(
            willData.beneficiaries.map(beneficiary => ({
              will_id: willId,
              address: beneficiary.address,
              name: beneficiary.name,
              email: beneficiary.email,
              relationship: beneficiary.relationship,
              share_percentage: beneficiary.sharePercentage,
              access_level: 'read',
              is_active: true
            }))
          );

        if (beneficiaryError) {
          console.error('Error creating beneficiaries:', beneficiaryError);
          throw new Error(`Failed to add beneficiaries: ${beneficiaryError.message}`);
        }
      }

      console.log('Will created successfully:', will);
      return will;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wills'] });
      toast.success('Will created successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to create will:', error);
      toast.error(error.message || 'Failed to create will');
    },
  });

  // Delete will mutation
  const deleteWillMutation = useMutation({
    mutationFn: async (willId: string) => {
      console.log('Deleting will:', willId);
      
      const { error } = await supabase
        .from('wills')
        .delete()
        .eq('id', willId);

      if (error) {
        console.error('Error deleting will:', error);
        throw new Error(error.message);
      }
      
      console.log('Will deleted successfully:', willId);
      return willId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wills'] });
      toast.success('Will deleted successfully');
    },
    onError: (error: any) => {
      console.error('Failed to delete will:', error);
      toast.error(error.message || 'Failed to delete will');
    },
  });

  // Update will mutation
  const updateWillMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<WillDocument> }) => {
      const { data, error } = await supabase
        .from('wills')
        .update({
          ...(updates.title && { title: updates.title }),
          ...(updates.description && { description: updates.description }),
          ...(updates.isActive !== undefined && { is_active: updates.isActive }),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating will:', error);
        throw new Error(error.message);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wills'] });
      toast.success('Will updated successfully');
    },
    onError: (error: any) => {
      console.error('Failed to update will:', error);
      toast.error(error.message || 'Failed to update will');
    },
  });

  const createWill = (willData: Parameters<typeof createWillMutation.mutate>[0]) => {
    createWillMutation.mutate(willData);
  };

  const deleteWill = async (willId: string, willTitle?: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the will "${willTitle || 'this will'}"? This action cannot be undone.`
    );
    
    if (confirmed) {
      deleteWillMutation.mutate(willId);
    }
  };

  const updateWill = async (id: string, updates: Partial<WillDocument>) => {
    return updateWillMutation.mutateAsync({ id, updates });
  };

  return {
    wills,
    isLoading,
    error,
    refetch,
    createWill,
    deleteWill,
    updateWill,
    isCreatingWill: createWillMutation.isPending,
    isDeletingWill: deleteWillMutation.isPending,
    isUpdatingWill: updateWillMutation.isPending,
  };
};